import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logoFull from '../assets/images/logo.svg';
import { TenantAppearanceDock } from '../components/tenant/TenantAppearanceDock';
import { buildApiUrl } from '../config/api';
import { storeAssistedLoginSessionToken } from '../services/assistedLoginSession';

export default function AssistedLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token')?.trim() ?? '';
  const [error, setError] = useState<string | null>(token ? null : 'This login link is missing a code.');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(buildApiUrl('/public/assisted-login/redeem'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (response) => {
        const payload = (await response.json().catch(() => null)) as {
          success?: boolean;
          message?: string;
          data?: { sessionToken?: string };
        } | null;
        if (!response.ok || !payload?.data?.sessionToken) {
          throw new Error(payload?.message || 'This login link is invalid or has expired.');
        }
        return payload.data.sessionToken;
      })
      .then((sessionToken) => {
        if (cancelled) return;
        storeAssistedLoginSessionToken(sessionToken);
        navigate('/my-information', { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'This login link is invalid or has expired.');
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, token]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
        <TenantAppearanceDock />
        <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200/80 shadow-lg p-8 text-center dark:bg-gray-800 dark:border-white/10 dark:shadow-none">
          <img src={logoFull} alt="Amber Peak" className="h-10 w-auto mx-auto mb-6" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Login link expired</h1>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <Link
            to="/?intent=prospect"
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-red-700 px-4 py-3 text-sm font-semibold text-white hover:bg-red-600"
          >
            Sign in another way
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <TenantAppearanceDock />
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Signing you in…</p>
      </div>
    </div>
  );
}
